import type { ReactNode } from 'react';
import { ApiError } from '../api/client';
import type { AsyncState } from '../useFetch';

/** Render one of loading / error / data for an async state, so views stay declarative. */
export function Async<T>({
  state,
  children,
}: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
}): ReactNode {
  if (state.loading) return <p className="muted">Loading…</p>;
  if (state.error) {
    const e = state.error;
    // When the API explained itself, say what it said. A 403 is NOT always "no permission" — it is
    // also how the platform reports `site_limit_reached` and `entitlement_restricted`, and calling
    // those a permission problem sends the reader hunting for the wrong fix. Only fall back to our
    // own words when the API gave none. (The shell handles a 401 by rendering the sign-in screen,
    // so this copy is the belt-and-braces case.)
    const msg =
      e instanceof ApiError && e.explained
        ? e.message
        : e instanceof ApiError && e.status === 401
          ? 'Not signed in — your session may have expired. Sign in to continue.'
          : e instanceof ApiError && e.status === 403
            ? 'You don’t have permission to view this.'
            : e.message;
    return <p className="error">{msg}</p>;
  }
  if (state.data === null) return null;
  return <>{children(state.data)}</>;
}
